import React, { useEffect, useCallback, useState } from "react";
import { useParams } from "react-router-dom";
import {
  useDesignFormStore,
  FabricUsageRow,
  SizeSegment,
} from "../../store/DesignMasterStore";
import axiosApi from "../../server/service";
import { Plus, Trash2, ChevronDown } from "lucide-react";

type DesignFormState = ReturnType<typeof useDesignFormStore.getState>;
type FabricUsageCategoryKey = keyof Omit<
  DesignFormState["fabricUsage"],
  "sizeSegments"
>;

// --- Reusable Component for each Table/Category ---
interface FabricUsageCategoryProps {
  title: string;
  categoryKey: keyof Omit<DesignFormState["fabricUsage"], "sizeSegments">;
  sizeSegments: SizeSegment[];
  rows: FabricUsageRow[];
  hasDropdown?: boolean;
  dropdownPlaceholder?: string;
  options?: { id: string; label: string }[]; // Dropdown ke options
  selectedValue?: string | null; // Selected value
  onSelectionChange?: (value: string | null) => void;
}

const FabricUsageCategory: React.FC<FabricUsageCategoryProps> = ({
  title,
  categoryKey,
  sizeSegments,
  rows,
  hasDropdown,
  dropdownPlaceholder,
  options = [],
  selectedValue,
  onSelectionChange,
}) => {
  const {
    addFabricUsageRow,
    updateFabricUsageRow,
    removeFabricUsageRow,
    getFormData,
  } = useDesignFormStore();

  const fabricusages = getFormData();

  // Function to calculate the total for each size column
  const calculateTotal = (sizeId: string) => {
    return rows
      .reduce((acc, row) => acc + (parseFloat(row.values[sizeId]) || 0), 0)
      .toFixed(2);
  };

  const handleNumericChange = (
    rowIndex: number,
    segmentId: string,
    value: string
  ) => {
    // Regex to allow numbers with up to 2 decimal places
    const regex = /^\d*\.?\d{0,2}$/;
    if (value === "" || regex.test(value)) {
      updateFabricUsageRow(categoryKey, rowIndex, segmentId, value);
    }
  };

  return (
    <div className="bg-white p-4 sm:p-6 rounded-md shadow-sm  mb-8">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-4">
        <h3 className="text-xl font-bold text-gray-800">{title}</h3>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full sm:w-auto">
          {hasDropdown && (
            <select
              value={selectedValue || ""}
              onChange={(e) => onSelectionChange?.(e.target.value || null)}
              className="w-full sm:w-auto px-3 py-2 border border-gray-400 shadow-sm rounded-md focus:ring-2 "
            >
              <option value="">{dropdownPlaceholder || "Select..."}</option>
              {options.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          )}
          <button
            type="button"
            onClick={() => addFabricUsageRow(categoryKey)}
            className="flex items-center border-2  border-gray-500 rounded-md justify-center gap-2 text-sm  font-semibold hover:text-blue-800 px-3 py-2 whitespace-nowrap"
          >
            <Plus className="w-4 h-4" />
            Add Component
          </button>
        </div>
      </div>

      <div className="overflow-x-auto ">
        <table className="w-full  text-sm  ">
          <thead className="bg-gray-50 h-10 text-gray-700 font-semibold  ">
            <tr>
              <th className="border border-gray-300 rounded-md  p-2 text-left w-3/12">
                Component
              </th>
              {sizeSegments.map((segment) => (
                <th
                  key={segment.id}
                  className="border border-gray-300 rounded-md font-medium p-2 text-center w-2/12"
                >
                  {segment.name}
                </th>
              ))}
              <th className="border border-gray-300 rounded-md p-2 text-center w-[10%]">
                Action
              </th>
            </tr>
          </thead>

          <tbody>
            {rows.map((row, rowIndex) => (
              <tr key={row.id} className="hover:bg-gray-50">
                {/* Component Name */}
                <td className="border border-gray-300 font-medium rounded-md  p-2">
                  <input
                    type="text"
                    placeholder="Component name"
                    value={row.component}
                    onChange={(e) =>
                      updateFabricUsageRow(
                        categoryKey,
                        rowIndex,
                        "component",
                        e.target.value
                      )
                    }
                    className="w-full h-10 border  border-gray-300 rounded-md  px-2 py-1"
                  />
                </td>

                {/* Dynamic Size Columns */}
                {sizeSegments.map((segment) => (
                  <td
                    key={segment.id}
                    className="border font-medium border-gray-300 rounded-md  p-2 text-center"
                  >
                    <input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={row.values[segment.id] || ""}
                      // onChange={(e) =>
                      //   updateFabricUsageRow(
                      //     categoryKey,
                      //     rowIndex,
                      //     segment.id,
                      //     e.target.value
                      //   )
                      // }
                      onChange={(e) =>
                        handleNumericChange(
                          rowIndex,
                          segment.id,
                          e.target.value
                        )
                      }
                      className="w-full h-10 border  border-gray-300  rounded-md px-2 py-1 text-center"
                    />
                  </td>
                ))}

                {/* Delete Button */}
                <td className="border border-gray-300 rounded-md  p-2 text-center">
                  {!row.isPredefined && (
                    <button
                      type="button"
                      onClick={() =>
                        removeFabricUsageRow(categoryKey, rowIndex)
                      }
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>

          {/* Total Row */}
          <tfoot>
            <tr className="bg-blue-50 h-10 font-semibold text-blue-700">
              <td className="border border-gray-300 rounded-md p-2">
                Total (Auto-calculated)
              </td>
              {sizeSegments.map((segment) => (
                <td
                  key={segment.id}
                  className="border border-gray-300 rounded-md  p-2 text-center"
                >
                  {calculateTotal(segment.id)}
                </td>
              ))}
              <td className="border border-gray-300 rounded-md p-2"></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

// --- Main Fabric Usage Tab Component ---
const FabricUsageTab: React.FC = () => {
  const { designId } = useParams();
  const {
    fabricUsage,
    notes,
    setSizeSegments,
    updateNotes,
    setCategoryOptions,
    setCategorySelection,
  } = useDesignFormStore();

  console.log("fabricUsage", fabricUsage);

  const {
    sizeSegments,
    fabricComponents,
    designWork,
    astar,
    dupatta,

    rate,
    handwork,
    embroidery,
    manualEmbroidery,
    stitching,
    accessories,
    additionalComponent,
  } = fabricUsage;

  // State for size segment filtering
  const [allAvailableSegments, setAllAvailableSegments] = useState<
    SizeSegment[]
  >([]);
  const [selectedSegmentIds, setSelectedSegmentIds] = useState<Set<string>>(
    new Set()
  );
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  // Fetch all available size segments with design usage info
  const fetchAllSizeSegments = useCallback(async () => {
    try {
      let activeSegments: SizeSegment[] = [];
      let segmentsToSelect: string[] = [];

      if (designId) {
        // Use the new API that includes isBeingUsedForThisDesign flag
        const res = await axiosApi.get<{
          message: string;
          data: Array<{
            _id: string;
            segmentName: string;
            status: string;
            numberOfSets: number;
            isBeingUsedForThisDesign: boolean;
          }>;
        }>(`/api/sizeSegment/get-segments-with-design-usage/${designId}`);

        activeSegments = res.data.data
          .filter((s) => s.status === "Active")
          .map((s) => ({
            id: s._id,
            name: s.segmentName,
          }));

        // Select segments where isBeingUsedForThisDesign is true
        segmentsToSelect = res.data.data
          .filter((s) => s.status === "Active" && s.isBeingUsedForThisDesign)
          .map((s) => s._id);
      } else {
        // For new designs, use the regular API
        const res = await axiosApi.get<
          Array<{
            _id: string;
            segmentName: string;
            status: string;
            numberOfSets: number;
          }>
        >("/api/sizeSegment/get-all-segments");

        activeSegments = res.data
          .filter((s) => s.status === "Active")
          .map((s) => ({
            id: s._id,
            name: s.segmentName,
          }));

        // For new designs, select all active segments
        segmentsToSelect = activeSegments.map((s) => s.id);
      }

      setAllAvailableSegments(activeSegments);
      // Initialize with segments to select
      setSelectedSegmentIds((prev) => {
        if (prev.size === 0 && segmentsToSelect.length > 0) {
          return new Set(segmentsToSelect);
        }
        return prev;
      });
    } catch (err) {
      console.error("Failed to fetch size segments:", err);
    }
  }, [designId]);

  const fetchAndSetVendors = useCallback(async () => {
    try {
      const res = await axiosApi.get<{
        data: { _id: string; vendorName: string; vendorType: string }[];
      }>("/api/vendors/getVendorsForDropdown");
      const allVendors = res.data.data;

      // Helper function to filter and map vendors
      const getOptionsForType = (type: string) => {
        return allVendors
          .filter((v) => v.vendorType === type)
          .map((v) => ({ id: v._id, label: v.vendorName }));
      };

      // Set options for each category in the Zustand store
      setCategoryOptions("astar", getOptionsForType("Fabric Supplier"));
      setCategoryOptions("dupatta", getOptionsForType("Fabric Supplier"));
      setCategoryOptions("handwork", getOptionsForType("Handwork"));
      setCategoryOptions("embroidery", getOptionsForType("Embroidery"));
      setCategoryOptions("manualEmbroidery", getOptionsForType("Embroidery"));
      setCategoryOptions("stitching", getOptionsForType("Stitching"));
      setCategoryOptions("accessories", getOptionsForType("Accessories"));
      setCategoryOptions("designWork", getOptionsForType("Fabric Supplier"));

      const allVendorOptions = allVendors.map((v) => ({
        id: v._id,
        label: v.vendorName,
      }));
      setCategoryOptions("additionalComponent", allVendorOptions);
    } catch (err) {
      console.error("Failed to fetch vendors:", err);
      // Add toast notification for error here
    }
  }, [setCategoryOptions]);

  useEffect(() => {
    fetchAllSizeSegments();
    fetchAndSetVendors();
  }, [fetchAllSizeSegments, fetchAndSetVendors]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Toggle segment selection
  const toggleSegmentSelection = (segmentId: string) => {
    setSelectedSegmentIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(segmentId)) {
        newSet.delete(segmentId);
      } else {
        newSet.add(segmentId);
      }
      return newSet;
    });
  };

  // Filter size segments based on selection
  const filteredSizeSegments =
    selectedSegmentIds.size > 0
      ? sizeSegments.filter((seg) => selectedSegmentIds.has(seg.id))
      : sizeSegments;

  if (!sizeSegments) {
    return <div className="text-center py-10 text-gray-500">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">
          Fabric Usage Configuration
        </h2>
        {/* Multi-select dropdown for size segments */}
        <div ref={dropdownRef} className="relative">
          <button
            type="button"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 shadow-sm"
          >
            <span className="text-sm font-medium text-gray-700">
              {selectedSegmentIds.size === 0
                ? "Select Size Segments"
                : selectedSegmentIds.size === allAvailableSegments.length
                ? "All Segments"
                : `${selectedSegmentIds.size} Selected`}
            </span>
            <ChevronDown
              className={`w-4 h-4 text-gray-500 transition-transform ${
                isDropdownOpen ? "rotate-180" : ""
              }`}
            />
          </button>
          {isDropdownOpen && (
            <div className="absolute right-0 mt-2 w-64 bg-white border border-gray-300 rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto">
              <div className="p-2">
                <div className="flex items-center justify-between mb-2 px-2 py-1">
                  <span className="text-sm font-semibold text-gray-700">
                    Size Segments
                  </span>
                  {selectedSegmentIds.size > 0 && (
                    <button
                      type="button"
                      onClick={() => setSelectedSegmentIds(new Set())}
                      className="text-xs text-blue-600 hover:text-blue-800"
                    >
                      Clear All
                    </button>
                  )}
                </div>
                {allAvailableSegments.map((segment) => {
                  const isSelected = selectedSegmentIds.has(segment.id);
                  return (
                    <label
                      key={segment.id}
                      className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer rounded"
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSegmentSelection(segment.id)}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-800">
                        {segment.name}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      <FabricUsageCategory
        title="Fabric"
        categoryKey="fabricComponents"
        sizeSegments={filteredSizeSegments}
        rows={fabricComponents.rows}
      />
      <FabricUsageCategory
        title="Job Work"
        categoryKey="designWork"
        sizeSegments={filteredSizeSegments}
        rows={designWork.rows}
        hasDropdown
        dropdownPlaceholder="Select vendor..."
        options={designWork.options}
        selectedValue={designWork.selection}
        onSelectionChange={(value) => setCategorySelection("designWork", value)}
      />

      <FabricUsageCategory
        title="Astar"
        categoryKey="astar"
        sizeSegments={filteredSizeSegments}
        rows={astar.rows}
        hasDropdown
        dropdownPlaceholder="Select astar vendor..."
        options={astar.options}
        selectedValue={astar.selection}
        onSelectionChange={(value) => setCategorySelection("astar", value)}
      />

      <FabricUsageCategory
        title="Dupatta "
        categoryKey="dupatta"
        sizeSegments={filteredSizeSegments}
        rows={dupatta.rows}
        hasDropdown
        dropdownPlaceholder="Select dupatta vendor..."
        options={dupatta.options}
        selectedValue={dupatta.selection}
        onSelectionChange={(value) => setCategorySelection("dupatta", value)}
      />

      <FabricUsageCategory
        title="Rate"
        categoryKey="rate"
        sizeSegments={filteredSizeSegments}
        rows={rate.rows}
      />
      <FabricUsageCategory
        title="Handwork"
        categoryKey="handwork"
        sizeSegments={filteredSizeSegments}
        rows={handwork.rows}
        hasDropdown
        dropdownPlaceholder="Select handwork..."
        options={handwork.options}
        selectedValue={handwork.selection}
        onSelectionChange={(value) => setCategorySelection("handwork", value)}
      />
      <FabricUsageCategory
        title="Manual Embroidery"
        categoryKey="manualEmbroidery"
        sizeSegments={filteredSizeSegments}
        rows={manualEmbroidery.rows}
        hasDropdown
        dropdownPlaceholder="Select embroidery..."
        options={manualEmbroidery.options}
        selectedValue={manualEmbroidery.selection}
        onSelectionChange={(value) =>
          setCategorySelection("manualEmbroidery", value)
        }
      />
      <FabricUsageCategory
        title="Stitching"
        categoryKey="stitching"
        sizeSegments={filteredSizeSegments}
        rows={stitching.rows}
        hasDropdown
        dropdownPlaceholder="Select stitching vendor..."
        options={stitching.options}
        selectedValue={stitching.selection}
        onSelectionChange={(value) => setCategorySelection("stitching", value)}
      />
      <FabricUsageCategory
        title="Accessories"
        categoryKey="accessories"
        sizeSegments={filteredSizeSegments}
        rows={accessories.rows}
        hasDropdown
        dropdownPlaceholder="Select accessories..."
        options={accessories.options}
        selectedValue={accessories.selection}
        onSelectionChange={(value) =>
          setCategorySelection("accessories", value)
        }
      />

      <FabricUsageCategory
        title="Additional"
        categoryKey="additionalComponent"
        sizeSegments={filteredSizeSegments}
        rows={additionalComponent.rows}
        hasDropdown
        dropdownPlaceholder="Select vendor..."
        options={additionalComponent.options}
        selectedValue={additionalComponent.selection}
        onSelectionChange={(value) =>
          setCategorySelection("additionalComponent", value)
        }
      />

      {/* Notes and Remarks Section */}
      <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border border-gray-300">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label
              htmlFor="specialCuttingInstructions"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Special Cutting Instructions
            </label>
            <textarea
              id="specialCuttingInstructions"
              rows={4}
              value={notes.specialCuttingInstructions}
              onChange={(e) =>
                updateNotes("specialCuttingInstructions", e.target.value)
              }
              className="w-full p-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter special cutting notes..."
            />
          </div>
          <div>
            <label
              htmlFor="notesAndRemarks"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Notes / Remarks
            </label>
            <textarea
              id="notesAndRemarks"
              rows={4}
              value={notes.notesAndRemarks}
              onChange={(e) => updateNotes("notesAndRemarks", e.target.value)}
              className="w-full p-2 border   rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter additional notes and remarks..."
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default FabricUsageTab;
